import { useMutation } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { Spinner } from "../../components/ui";
import { cn } from "../../lib/cn";
import { useToast } from "../../components/Toast";
import { errorMessage } from "../../lib/errors";

interface PokeResponse {
  ok?: boolean;
  throttled?: boolean;
  error?: string;
  sent?: number;
}

/**
 * "👀 {name} is watching".
 *
 * The payload is built server-side from the sender's profile, so this button
 * cannot be used to push arbitrary text at a friend, and the Edge Function caps
 * it at one poke per person per day.
 */
export function PokeButton({
  targetId,
  targetName,
  goalId,
  className,
}: {
  targetId: string;
  targetName: string;
  goalId?: string;
  className?: string;
}) {
  const toast = useToast();

  const poke = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<PokeResponse>(
        "send-push",
        {
          body: { target_id: targetId, goal_id: goalId ?? null },
        },
      );

      // A 429 comes back as a FunctionsHttpError; the useful message is in the
      // response body, so read it rather than showing "non-2xx status code".
      if (error) {
        const body = await readErrorBody(error);
        throw new Error(body?.error ?? error.message);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      toast(
        data?.sent
          ? `Poked ${targetName} 👀`
          : `${targetName} has no devices set up for nudges`,
        data?.sent ? "success" : "info",
      );
    },
    onError: (error) => toast(errorMessage(error, "Could not poke"), "error"),
  });

  // The design draws this as an outline tag, not a button: the smallest,
  // least shouty affordance on the screen, which is the point of a poke.
  return (
    <button
      type="button"
      onClick={() => poke.mutate()}
      disabled={poke.isPending}
      className={cn(
        "tag tag-outline cursor-pointer transition-colors hover:bg-accent-100 disabled:opacity-45",
        className,
      )}
    >
      {poke.isPending ? (
        <Spinner className="size-3 border-t-current" />
      ) : (
        <span aria-hidden>👀</span>
      )}
      poke
    </button>
  );
}

async function readErrorBody(error: unknown): Promise<PokeResponse | null> {
  const response = (error as { context?: Response })?.context;
  if (!response || typeof response.json !== "function") return null;
  try {
    return (await response.json()) as PokeResponse;
  } catch {
    return null;
  }
}
